import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'

// Stati di Maturità Operativa
export type MaturityState = 'visibility' | 'stability' | 'control' | 'optimization'

export interface MaturityStateConfig {
  id: MaturityState
  nameIt: string
  nameEn: string
  descriptionIt: string
  descriptionEn: string
  impactIt: string
  impactEn: string
  color: string
  bgColor: string
  borderColor: string
}

export const MATURITY_STATES: Record<MaturityState, MaturityStateConfig> = {
  visibility: {
    id: 'visibility',
    nameIt: 'Visibilità',
    nameEn: 'Visibility',
    descriptionIt: 'Il sistema vede i tuoi movimenti finanziari',
    descriptionEn: 'The system sees your financial movements',
    impactIt: 'Le previsioni non sono ancora affidabili. Continua a caricare dati.',
    impactEn: 'Predictions are not yet reliable. Continue uploading data.',
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    borderColor: 'border-slate-300 dark:border-slate-600',
  },
  stability: {
    id: 'stability',
    nameIt: 'Stabilità',
    nameEn: 'Stability',
    descriptionIt: 'Spese ed entrate ricorrenti riconosciute',
    descriptionEn: 'Recurring income and expenses recognized',
    impactIt: 'Prime previsioni mensili disponibili.',
    impactEn: 'First monthly predictions available.',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  control: {
    id: 'control',
    nameIt: 'Controllo',
    nameEn: 'Control',
    descriptionIt: 'Cash flow previsto con alert preventivi',
    descriptionEn: 'Predicted cash flow with preventive alerts',
    impactIt: 'Rischi finanziari evidenziati in anticipo.',
    impactEn: 'Financial risks highlighted in advance.',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
  },
  optimization: {
    id: 'optimization',
    nameIt: 'Ottimizzazione',
    nameEn: 'Optimization',
    descriptionIt: 'Previsioni avanzate e suggerimenti proattivi',
    descriptionEn: 'Advanced predictions and proactive suggestions',
    impactIt: 'Supporto decisionale completo disponibile.',
    impactEn: 'Full decision support available.',
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800',
  },
}

// Criteri per la maturità
export interface MaturityCriterion {
  id: string
  nameIt: string
  nameEn: string
  descriptionIt: string
  descriptionEn: string
  status: 'complete' | 'partial' | 'missing'
  category: 'income' | 'expense' | 'pattern' | 'data'
  actionIt?: string
  actionEn?: string
}

export interface MaturityData {
  currentState: MaturityState
  criteria: MaturityCriterion[]
  pendingAction?: {
    id: string
    type: 'confirm_recurring' | 'categorize' | 'connect_source'
    titleIt: string
    titleEn: string
    descriptionIt: string
    descriptionEn: string
    entityId?: string
  }
  stats: {
    recurringExpensesCount: number
    recurringIncomeCount: number
    categorizedPercentage: number
    monthsCovered: number
    patternsRecognized: number
  }
}

export function useFinancialMaturity() {
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)

  // Query per i dati di maturità - ora usa l'endpoint dedicato
  const { data: maturityData, isLoading, refetch } = useQuery<MaturityData>({
    queryKey: ['financial-maturity'],
    queryFn: async () => {
      // Chiama l'endpoint dedicato che calcola tutto lato server
      const response = await api.get('/analytics/maturity')
      const data = response.data
      
      // Converti i criteri nel formato atteso
      interface RawCriterion {
        id: string
        nameIt: string
        nameEn: string
        descriptionIt: string
        descriptionEn: string
        status: string
      }
      const criteria: MaturityCriterion[] = (data.criteria || []).map((c: RawCriterion) => ({
        id: c.id,
        nameIt: c.nameIt,
        nameEn: c.nameEn,
        descriptionIt: c.descriptionIt,
        descriptionEn: c.descriptionEn,
        status: c.status as 'complete' | 'partial' | 'missing',
        category: c.id.includes('income') ? 'income' : 
                  c.id.includes('expense') ? 'expense' : 
                  c.id.includes('pattern') ? 'pattern' : 'data',
      }))

      return {
        currentState: data.currentState as MaturityState,
        criteria,
        stats: data.stats,
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minuti
    retry: 1,
  })

  // Azione confermata
  const confirmAction = useCallback(async (actionId: string) => {
    try {
      await api.post(`/analytics/maturity/confirm`, { actionId })
      setFeedbackMessage('Affidabilità delle previsioni migliorata')
      setTimeout(() => setFeedbackMessage(null), 3000)
      refetch()
    } catch {
      // Feedback comunque per UX
      setFeedbackMessage('Azione registrata')
      setTimeout(() => setFeedbackMessage(null), 3000)
    }
  }, [refetch])

  // Ignora azione
  const ignoreAction = useCallback(async (actionId: string) => {
    try {
      await api.post(`/analytics/maturity/ignore`, { actionId })
      refetch()
    } catch {
      // Silent fail
    }
  }, [refetch])

  // Stato corrente con configurazione
  const currentStateConfig = useMemo(() => {
    if (!maturityData) return MATURITY_STATES.visibility
    return MATURITY_STATES[maturityData.currentState]
  }, [maturityData])

  return {
    maturityData,
    currentStateConfig,
    isLoading,
    feedbackMessage,
    confirmAction,
    ignoreAction,
    refetch,
    MATURITY_STATES,
  }
}
